import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from './AuthContext';
import { getAvisosAtivos } from '../services/firestoreService';
import {
  loadLegacyUnreadNotifications,
  listenNotificationReadReceipts,
  listenServidorNotificationCenter,
  listenServidorUnreadNotificationFlag,
  loadMoreServidorNotificationCenter,
  mergeServidorNotifications,
  normalizeAvisoForNotificationCenter,
  setServidorNotificationRead,
} from '../services/notificationCenterService';

const NotificationCenterContext = createContext(null);
const AVISOS_REFRESH_MS = 15 * 60 * 1000;

export const NotificationCenterProvider = ({ children }) => {
  const { authReady, isAuthenticated, user } = useAuth();
  const [centralItems, setCentralItems] = useState([]);
  const [centralHistoryItems, setCentralHistoryItems] = useState([]);
  const [centralCursor, setCentralCursor] = useState(null);
  const [hasMoreCentral, setHasMoreCentral] = useState(false);
  const [loadingMoreCentral, setLoadingMoreCentral] = useState(false);
  const [hasUnreadCanonical, setHasUnreadCanonical] = useState(false);
  const historyLoadedRef = useRef(false);
  const activeSessionRef = useRef('');
  const centralItemsRef = useRef([]);
  const [legacyItems, setLegacyItems] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [avisoReceipts, setAvisoReceipts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uid = String(user?.uid || user?.id || '').trim();
  const municipioId = String(user?.municipioId || '').trim();
  const cpf = String(user?.cpf || user?.doc_cpf || '').replace(/\D/g, '').slice(0, 11);

  useEffect(() => {
    if (!authReady || !isAuthenticated || !uid || !municipioId) {
      setCentralItems([]);
      setCentralHistoryItems([]);
      setCentralCursor(null);
      setHasMoreCentral(false);
      setLoadingMoreCentral(false);
      setHasUnreadCanonical(false);
      historyLoadedRef.current = false;
      centralItemsRef.current = [];
      activeSessionRef.current = '';
      setLegacyItems([]);
      setAvisos([]);
      setAvisoReceipts(new Map());
      setLoading(false);
      setError(null);
      return undefined;
    }

    const sessionKey = `${uid}:${municipioId}`;
    activeSessionRef.current = sessionKey;
    // Nunca reutilizar visualmente dados da sessão anterior enquanto os novos
    // listeners ainda estão conectando (aparelho compartilhado/troca de vínculo).
    setCentralItems([]);
    centralItemsRef.current = [];
    setCentralHistoryItems([]);
    setLegacyItems([]);
    setAvisos([]);
    setAvisoReceipts(new Map());
    setHasUnreadCanonical(false);
    setLoading(true);
    setError(null);
    setCentralCursor(null);
    setHasMoreCentral(false);
    setLoadingMoreCentral(false);
    historyLoadedRef.current = false;
    let centralReady = false;
    let legacyReady = false;
    const updateLoading = () => setLoading(!(centralReady && legacyReady));
    const handleError = (listenerError) => {
      console.warn('Falha ao sincronizar a Central de Notificações:', listenerError);
      setError('Algumas notificações podem demorar para aparecer.');
      setLoading(false);
    };

    const unsubscribeCentral = listenServidorNotificationCenter({
      uid,
      onChange: (items, pageInfo = {}) => {
        centralReady = true;
        if (historyLoadedRef.current && centralItemsRef.current.length > 0) {
          const nextIds = new Set(items.map((item) => item.id));
          const displacedItems = centralItemsRef.current.filter((item) => !nextIds.has(item.id));
          if (displacedItems.length > 0) {
            setCentralHistoryItems((current) => mergeServidorNotifications(current, displacedItems));
          }
        }
        centralItemsRef.current = items;
        setCentralItems(items);
        if (!historyLoadedRef.current) {
          setCentralCursor(pageInfo.cursor || null);
          setHasMoreCentral(Boolean(pageInfo.hasMore));
        }
        updateLoading();
      },
      onError: handleError,
    });
    const unsubscribeUnreadFlag = listenServidorUnreadNotificationFlag({
      uid,
      onChange: setHasUnreadCanonical,
      onError: (unreadError) => {
        console.warn('Falha ao verificar notificações não lidas:', unreadError);
      },
    });
    let disposed = false;
    loadLegacyUnreadNotifications({municipioId, uid, cpf})
      .then((items) => {
        if (disposed) return;
        legacyReady = true;
        setLegacyItems(items);
        updateLoading();
      })
      .catch((legacyError) => {
        if (disposed) return;
        legacyReady = true;
        handleError(legacyError);
        updateLoading();
      });

    return () => {
      disposed = true;
      if (activeSessionRef.current === sessionKey) activeSessionRef.current = '';
      unsubscribeCentral();
      unsubscribeUnreadFlag();
    };
  }, [authReady, cpf, isAuthenticated, municipioId, uid]);

  useEffect(() => {
    if (!authReady || !isAuthenticated || !uid || !municipioId) return undefined;
    let disposed = false;
    let lastFetchedAt = 0;

    const refreshAvisos = async (forceRefresh = false) => {
      if (!forceRefresh && Date.now() - lastFetchedAt < AVISOS_REFRESH_MS) return;
      try {
        const items = await getAvisosAtivos(municipioId, {forceRefresh});
        if (!disposed) {
          setAvisos(items);
          lastFetchedAt = Date.now();
        }
      } catch (fetchError) {
        if (!disposed) {
          console.warn('Não foi possível agregar os avisos à Central de Notificações:', fetchError);
          setError((current) => current || 'Os avisos oficiais podem demorar para aparecer na Central.');
        }
      }
    };

    refreshAvisos(false);
    const handleNotificationReceived = (event) => {
      const type = String(event?.detail?.tipo || event?.detail?.eventType || '').toLowerCase();
      if (type.includes('aviso')) refreshAvisos(true);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshAvisos(false);
    };
    window.addEventListener('sisfol:notification-received', handleNotificationReceived);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      window.removeEventListener('sisfol:notification-received', handleNotificationReceived);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [authReady, isAuthenticated, municipioId, uid]);

  useEffect(() => {
    if (!authReady || !isAuthenticated || !uid || avisos.length === 0) {
      setAvisoReceipts(new Map());
      return undefined;
    }

    return listenNotificationReadReceipts({
      uid,
      entityIds: avisos.map((aviso) => aviso?.id).filter(Boolean),
      onChange: setAvisoReceipts,
      onError: (receiptError) => {
        console.warn('Falha ao sincronizar leitura dos avisos:', receiptError);
        setError((current) => current || 'A leitura dos avisos pode demorar para sincronizar.');
      },
    });
  }, [authReady, avisos, isAuthenticated, uid]);

  const avisoItems = useMemo(() => avisos
    .filter((aviso) => String(aviso?.notificationPolicy || aviso?.pushPolicy || 'central_push').toLowerCase() !== 'none')
    .map((aviso) => (
      normalizeAvisoForNotificationCenter(aviso, avisoReceipts.get(String(aviso.id)) === true)
    )), [avisos, avisoReceipts]);

  const notifications = useMemo(
    () => mergeServidorNotifications(
      [...centralItems, ...centralHistoryItems],
      [...legacyItems, ...avisoItems],
    ),
    [avisoItems, centralHistoryItems, centralItems, legacyItems],
  );
  const unreadCount = useMemo(
    () => notifications.reduce((total, item) => total + (item.read ? 0 : 1), 0),
    [notifications],
  );
  const hasUnread = hasUnreadCanonical || unreadCount > 0;

  const loadMore = useCallback(async () => {
    if (!uid || !municipioId || !centralCursor || !hasMoreCentral || loadingMoreCentral) return;
    const sessionKey = `${uid}:${municipioId}`;
    setLoadingMoreCentral(true);
    try {
      const page = await loadMoreServidorNotificationCenter({
        uid,
        afterSnapshot: centralCursor,
      });
      if (activeSessionRef.current !== sessionKey) return;
      historyLoadedRef.current = true;
      setCentralHistoryItems((current) => mergeServidorNotifications(current, page.items));
      setCentralCursor(page.cursor || centralCursor);
      setHasMoreCentral(Boolean(page.hasMore));
    } catch (pageError) {
      if (activeSessionRef.current !== sessionKey) return;
      console.warn('Não foi possível carregar notificações anteriores:', pageError);
      setError((current) => current || 'Não foi possível carregar notificações anteriores agora.');
    } finally {
      if (activeSessionRef.current === sessionKey) setLoadingMoreCentral(false);
    }
  }, [centralCursor, hasMoreCentral, loadingMoreCentral, municipioId, uid]);

  const markRead = useCallback(async (item, read = true) => {
    const sessionKey = `${uid}:${municipioId}`;
    await setServidorNotificationRead({uid, municipioId, item, read});
    if (activeSessionRef.current !== sessionKey) return;
    if (item?.storage === 'notificationCenter') {
      const updateRead = (current) => current.map((entry) => (
        entry.id === item.id ? {...entry, read: Boolean(read)} : entry
      ));
      centralItemsRef.current = updateRead(centralItemsRef.current);
      setCentralItems(updateRead);
      setCentralHistoryItems(updateRead);
    } else if (item?.storage === 'legacyNotification') {
      setLegacyItems((current) => current.map((entry) => (
        entry.id === item.id ? {...entry, read: Boolean(read)} : entry
      )));
    } else if (item?.storage === 'aviso' && read) {
      setAvisoReceipts((current) => {
        const next = new Map(current);
        next.set(String(item.id), true);
        return next;
      });
    }
  }, [municipioId, uid]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    hasUnread,
    loading,
    error,
    markRead,
    loadMore,
    hasMore: hasMoreCentral,
    loadingMore: loadingMoreCentral,
  }), [
    error,
    hasMoreCentral,
    hasUnread,
    loadMore,
    loading,
    loadingMoreCentral,
    markRead,
    notifications,
    unreadCount,
  ]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);
  if (!context) throw new Error('useNotificationCenter deve ser usado dentro de NotificationCenterProvider.');
  return context;
};
